import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search, Flame, Tag as TagIcon, Clock, Eye, ChevronLeft, ChevronRight } from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, SectionHead, Rows, Empty } from '../components/UI'
import { get, qs } from '../lib/api'
import { timeAgo, asArray, num } from '../lib/format'
import { useSeo } from '../lib/seo'

const GRADIENTS = [
  'from-brand-600/45 via-brand-500/10 to-aqua/25',
  'from-aqua/40 via-white/[.04] to-brand-500/25',
  'from-up/35 via-white/[.03] to-brand-600/25',
  'from-gold/35 via-white/[.03] to-down/20',
]

const PER_PAGE = 9

function Cover({ post, i, className }: { post: any; i: number; className?: string }) {
  if (post?.coverUrl) {
    return (
      <img
        src={post.coverUrl}
        alt={post.coverAlt || post.title}
        loading="lazy"
        decoding="async"
        className={`w-full object-cover ${className || 'h-36 rounded-xl'}`}
      />
    )
  }
  return <div className={`bg-gradient-to-br ${GRADIENTS[i % GRADIENTS.length]} ${className || 'h-36 rounded-xl'}`} />
}

function Meta({ post, cfg }: { post: any; cfg?: any }) {
  // پیکربندی هاب می‌تواند نمایش بازدید/تاریخ را خاموش کند؛ پیش‌فرض روشن است
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
      <span className="chip !py-0.5 text-[11px] text-brand-200">{post.category}</span>
      <span className="inline-flex items-center gap-1">
        <Clock className="h-3 w-3" /> {num(post.readingMinutes || 1)} دقیقه
      </span>
      {cfg?.showViews !== false && post.views > 0 && (
        <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {num(post.views)}</span>
      )}
      {cfg?.showDate !== false && <span>{timeAgo(post.publishedAt)}</span>}
    </div>
  )
}

export default function Blog() {
  const [params, setParams] = useSearchParams()
  const category = params.get('category') || ''
  const tag = params.get('tag') || ''
  const q = params.get('q') || ''
  const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1)

  const [data, setData] = useState<any>(null)
  const [side, setSide] = useState<any>({ categories: [], tags: [] })
  const [featured, setFeatured] = useState<any[]>([])
  const [popular, setPopular] = useState<any[]>([])
  const [term, setTerm] = useState(q)
  const [loading, setLoading] = useState(true)
  // پیکربندی هاب نشریار؛ null یعنی «هنوز نیامده» تا فهرست با اندازه صفحه درست بار شود
  const [cfg, setCfg] = useState<any>(null)

  useEffect(() => setTerm(q), [q])

  const perPage = Math.min(Math.max(parseInt(String(cfg?.postsPerPage ?? ''), 10) || PER_PAGE, 1), 50)

  useEffect(() => {
    if (cfg === null) return // اول پیکربندی، بعد فهرست — تا اندازه صفحه دو بار عوض نشود
    let alive = true
    setLoading(true)
    get(`/content/posts${qs({ page, limit: perPage, category, tag, q })}`)
      .then((r) => { if (alive) setData(r) })
      .catch(() => { if (alive) setData({ items: [], meta: { pages: 1, total: 0 } }) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [page, category, tag, q, cfg, perPage])

  useEffect(() => {
    get('/content/blog-config')
      .then((r) => setCfg(r && typeof r === 'object' && !Array.isArray(r) ? r : {}))
      .catch(() => setCfg({}))
    get('/content/posts/categories').then(setSide).catch(() => setSide({ categories: [], tags: [] }))
    get('/content/posts/popular?limit=5')
      .then((r) => setPopular(asArray(r, 'items')))
      .catch(() => setPopular([]))
    get('/content/posts?featured=true&limit=3')
      .then((r) => setFeatured(asArray(r, 'items')))
      .catch(() => setFeatured([]))
  }, [])

  const items = asArray<any>(data, 'items')
  const meta = data?.meta || { page: 1, pages: 1, total: 0 }
  const tags = asArray<any>(side, 'tags')

  // ترتیب دسته‌ها طبق پیکربندی هاب؛ دسته‌های نیامده در آن، بعدشان می‌آیند
  const cats = useMemo(() => {
    const raw = asArray<any>(side, 'categories')
    const order: string[] = Array.isArray(cfg?.categoriesOrder)
      ? cfg.categoriesOrder.map((c: any) => String(c))
      : []
    if (!order.length) return raw
    const rank = (name: string) => {
      const i = order.indexOf(name)
      return i === -1 ? order.length : i
    }
    return [...raw].sort((a, b) => rank(a.name) - rank(b.name))
  }, [side, cfg])

  const menu = useMemo(
    () => (Array.isArray(cfg?.menu)
      ? cfg.menu.filter((m: any) => m && m.label && m.url).slice(0, 10)
      : []),
    [cfg],
  )

  const setParam = useCallback((patch: Record<string, string>) => {
    const next = new URLSearchParams(params)
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)))
    if (!('page' in patch)) next.delete('page')
    setParams(next)
  }, [params, setParams])

  // ---- سئوی صفحه فهرست — عنوان و توضیح پیش‌فرض از پیکربندی هاب می‌آید
  const baseTitle = String(cfg?.blogTitle || '').trim() || 'آموزش و اخبار ارز دیجیتال'
  const baseDesc = String(cfg?.blogDescription || '').trim()
    || 'آموزش خرید و فروش ارز دیجیتال، تحلیل بازار، امنیت کیف پول و تازه‌های رمزارز — به زبان ساده.'
  const heading = tag ? `مطالب با برچسب ${tag}` : category ? `${category}` : baseTitle
  const seoDesc = q
    ? `نتایج جستجوی «${q}» در وبلاگ ارزینو`
    : tag
      ? `مجموعه مقاله‌های مرتبط با ${tag} در وبلاگ ارزینو.`
      : category
        ? `آخرین مقاله‌های دسته ${category}: آموزش، تحلیل و اخبار بازار ارز دیجیتال.`
        : baseDesc

  const seoJson = useMemo(() => {
    // ناشر می‌تواند داده ساختاریافته را کلاً خاموش کند
    if (!items.length || cfg?.structuredData === false) return null
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: items.map((p: any, i: number) => ({
        '@type': 'ListItem',
        position: (page - 1) * perPage + i + 1,
        url: `${window.location.origin}/blog/${p.slug}`,
        name: p.title,
      })),
    }
  }, [items, page, perPage, cfg])

  useSeo({
    title: page > 1 ? `${heading} — صفحه ${page}` : heading,
    description: seoDesc,
    canonical: `/blog${qs({ category, tag, page: page > 1 ? page : '' })}`,
    // صفحه نتایج جستجو نباید ایندکس شود؛ برای گوگل محتوای تکراری و بی‌ارزش است
    noindex: !!q,
    // ناشر می‌تواند کل وبلاگ را از ایندکس بیرون ببرد؛ لینک‌ها دنبال می‌شوند
    noindexFollow: cfg?.blogNoindex === true,
    image: cfg?.defaultOgImage,
    twitterSite: cfg?.twitterHandle,
    googleVerification: cfg?.googleVerification,
    type: 'website',
    prev: page > 1 ? `/blog${qs({ category, tag, page: page - 1 > 1 ? page - 1 : '' })}` : undefined,
    next: page < (meta.pages || 1) ? `/blog${qs({ category, tag, page: page + 1 })}` : undefined,
    jsonLd: seoJson,
  }, [heading, seoDesc, page, category, tag, q, meta.pages, items.length, cfg])

  const clean = !category && !tag && !q && page === 1

  return (
    <Shell>
      <SectionHead
        title={heading}
        subtitle={
          String(cfg?.blogDescription || '').trim()
            ? String(cfg.blogDescription).trim()
            : meta.total
              ? `${num(meta.total)} مطلب — تحلیل‌ها، آموزش‌ها و تازه‌های بازار رمزارز`
              : 'تحلیل‌ها، آموزش‌ها و تازه‌های بازار رمزارز'
        }
      />

      {/* منوی وبلاگ — لینک‌های تعریف‌شده در هاب نشریار */}
      {menu.length > 0 && (
        <nav aria-label="منوی وبلاگ" className="mb-5 flex flex-wrap items-center gap-2">
          {menu.map((m: any, i: number) =>
            /^https?:\/\//i.test(String(m.url)) ? (
              <a key={`${m.url}-${i}`} href={m.url} target="_blank" rel="noopener noreferrer"
                 className="chip !py-1 text-[11px] text-slate-300 hover:text-white">
                {m.label}
              </a>
            ) : (
              <Link key={`${m.url}-${i}`} to={m.url}
                    className="chip !py-1 text-[11px] text-slate-300 hover:text-white">
                {m.label}
              </Link>
            ),
          )}
        </nav>
      )}

      {/* جستجو */}
      <form
        className="mb-6 flex flex-wrap gap-2"
        onSubmit={(e) => { e.preventDefault(); setParam({ q: term.trim() }) }}
      >
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="جستجو در مقاله‌ها…"
            className="input w-full !pr-10"
            aria-label="جستجو در وبلاگ"
          />
        </div>
        <button type="submit" className="btn-primary !px-5 text-sm">جستجو</button>
        {(q || category || tag) && (
          <button type="button" className="btn-ghost !px-4 text-xs"
                  onClick={() => setParams(new URLSearchParams())}>
            حذف فیلترها
          </button>
        )}
      </form>

      {/* مقاله‌های ویژه — فقط در نمای اصلی */}
      {clean && featured.length > 0 && (
        <div className="mb-8">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-300">
            <Flame className="h-4 w-4 text-gold" /> مطالب ویژه
          </h2>
          <div className="grid gap-5 lg:grid-cols-3">
            {featured.map((p: any, i: number) => (
              <Link key={p.slug} to={`/blog/${p.slug}`}>
                <Card hover className="flex h-full flex-col overflow-hidden !p-0">
                  <Cover post={p} i={i} className="h-40" />
                  <div className="flex flex-1 flex-col p-4">
                    <Meta post={p} cfg={cfg} />
                    <h3 className="mb-2 mt-3 font-bold leading-7 text-white">{p.title}</h3>
                    <p className="text-sm leading-7 text-slate-400">{p.excerpt}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr,17rem]">
        {/* ستون مقاله‌ها */}
        <div>
          {loading ? (
            <Rows n={6} h="h-52" />
          ) : items.length === 0 ? (
            <Empty text={q ? `نتیجه‌ای برای «${q}» پیدا نشد.` : 'هنوز مطلبی منتشر نشده است.'} />
          ) : (
            <div className="grid gap-5 sm:grid-cols-2">
              {items.map((p: any, i: number) => (
                <Link key={p.slug} to={`/blog/${p.slug}`}>
                  <article>
                    <Card hover className="flex h-full flex-col overflow-hidden !p-0">
                      <Cover post={p} i={i} className="h-40" />
                      <div className="flex flex-1 flex-col p-4">
                        <Meta post={p} cfg={cfg} />
                        <h2 className="mb-2 mt-3 font-bold leading-7 text-white">{p.title}</h2>
                        <p className="text-sm leading-7 text-slate-400">{p.excerpt}</p>
                        <span className="mt-4 text-xs font-semibold text-brand-300">ادامه مطلب →</span>
                      </div>
                    </Card>
                  </article>
                </Link>
              ))}
            </div>
          )}

          {/* صفحه‌بندی */}
          {meta.pages > 1 && (
            <nav className="mt-8 flex items-center justify-center gap-2" aria-label="صفحه‌بندی">
              <button
                className="btn-ghost !px-3 text-xs disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setParam({ page: String(page - 1) })}
              >
                <ChevronRight className="h-4 w-4" /> قبلی
              </button>
              <span className="text-xs text-slate-400">
                صفحه {num(page)} از {num(meta.pages)}
              </span>
              <button
                className="btn-ghost !px-3 text-xs disabled:opacity-40"
                disabled={page >= meta.pages}
                onClick={() => setParam({ page: String(page + 1) })}
              >
                بعدی <ChevronLeft className="h-4 w-4" />
              </button>
            </nav>
          )}
        </div>

        {/* ستون کناری */}
        <aside className="space-y-5">
          {cats.length > 0 && (
            <Card>
              <h3 className="mb-3 text-sm font-bold text-white">دسته‌بندی‌ها</h3>
              <ul className="space-y-1.5">
                {cats.map((c: any) => (
                  <li key={c.name}>
                    <button
                      onClick={() => setParam({ category: c.name === category ? '' : c.name, tag: '' })}
                      className={`flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-xs transition ${
                        c.name === category ? 'bg-brand-500/15 text-brand-200' : 'text-slate-400 hover:bg-white/5'
                      }`}
                    >
                      <span>{c.name}</span>
                      <span className="text-[11px] text-slate-500">{num(c.count)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {tags.length > 0 && (
            <Card>
              <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
                <TagIcon className="h-4 w-4 text-brand-300" /> برچسب‌ها
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((t: any) => (
                  <button
                    key={t.name}
                    onClick={() => setParam({ tag: t.name === tag ? '' : t.name, category: '' })}
                    className={`chip !py-1 text-[11px] transition ${
                      t.name === tag ? 'text-brand-200 ring-1 ring-brand-500/40' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </Card>
          )}

          {popular.length > 0 && (
            <Card>
              <h3 className="mb-3 text-sm font-bold text-white">پربازدیدترین‌ها</h3>
              <ul className="space-y-3">
                {popular.map((p: any, i: number) => (
                  <li key={p.slug}>
                    <Link to={`/blog/${p.slug}`} className="group flex gap-2">
                      <span className="text-lg font-black text-white/10">{num(i + 1)}</span>
                      <span className="text-xs leading-6 text-slate-400 group-hover:text-slate-200">
                        {p.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </aside>
      </div>
    </Shell>
  )
}
